import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core'
import { Router } from '@angular/router'
import { AnalyticsService } from '@app/core/_services/analytics.service'
import { ContentStackService } from '@app/core/_services/content-stack.service'
import { DialogService } from '@app/core/_services/dialog.service'
import { FeatureCheckService } from '@app/core/_services/feature-check.service'
import {
    FmLocalStorageItem,
    LocalStorageAccessService,
} from '@app/core/_services/local-storage-access.service'
import { ViewportService } from '@app/core/_services/viewport.service'
import { MessageModule } from 'primeng/message'

/** Set by the mobile × (either variant). Hides the banner on mobile only. */
const MOBILE_DISMISSED_KEY: FmLocalStorageItem = 'groupMeetings:tryNewBannerDismissed'
/** Set by the desktop × (non-host variant only). Hides the banner on desktop and mobile. */
const DESKTOP_DISMISSED_KEY: FmLocalStorageItem = 'groupMeetings:tryNewBannerDesktopDismissed'

@Component({
    selector: 'fm-group-meetings-sidebar',
    standalone: true,
    imports: [MessageModule],
    templateUrl: './group-meetings-sidebar.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'contents' },
})
export class GroupMeetingsSidebarComponent {
    readonly openInDynamicPanel = input(true)
    readonly groupMeetingsCreateEnabled = input<boolean | undefined>()
    /** Theme showcase: ignore stored dismissals and never persist or log a new one */
    readonly previewMode = input(false)

    private readonly storedMobileDismissed: boolean
    private readonly storedDesktopDismissed: boolean
    private readonly dismissedThisSession = signal(false)

    readonly isHost = computed(
        () =>
            this.groupMeetingsCreateEnabled() ??
            this.featureCheckService.groupMeetingsCreateEnabledSig(),
    )

    /** Mobile: both variants can be dismissed. Desktop: only the non-host promo. */
    readonly showDismissButton = computed(() => this.viewportService.isMobile() || !this.isHost())

    readonly isVisible = computed(() => {
        if (this.dismissedThisSession()) {
            return false
        }
        if (this.previewMode()) {
            return true
        }
        if (this.viewportService.isMobile()) {
            return !this.storedMobileDismissed && !this.storedDesktopDismissed
        }
        return this.isHost() || !this.storedDesktopDismissed
    })

    readonly pMessageScopedToken = {
        root: {
            borderRadius: '16px',
        },
        content: {
            gap: '7px',
            padding: '1px',
        },
        colorScheme: {
            dark: {
                info: {
                    background: '{blue.500}',
                    borderColor: '{blue.400}',
                    color: '{blue.50}',
                    shadow: '0 4px 8px 0 rgba(2, 5, 10, 0.04)',
                },
            },
            light: {
                info: {
                    background: '{blue.100}',
                    borderColor: '{blue.200}',
                    color: '{blue.600}',
                    shadow: '{form.field.shadow}',
                },
            },
        },
    }

    constructor(
        private analyticsService: AnalyticsService,
        private contentStackService: ContentStackService,
        private dialogService: DialogService,
        private featureCheckService: FeatureCheckService,
        private localStorageService: LocalStorageAccessService,
        private router: Router,
        public viewportService: ViewportService,
    ) {
        this.storedMobileDismissed =
            this.localStorageService.getItem(MOBILE_DISMISSED_KEY) === 'true'
        this.storedDesktopDismissed =
            this.localStorageService.getItem(DESKTOP_DISMISSED_KEY) === 'true'
    }

    private get analyticsSource(): 'groupMeetingsSidebar' | 'groupMeetingsMobileSidebar' {
        return this.openInDynamicPanel() ? 'groupMeetingsSidebar' : 'groupMeetingsMobileSidebar'
    }

    onClickGroupMeetings(): void {
        if (!this.featureCheckService.groupMeetingsEnabledSig()) {
            return
        }

        if (this.isHost()) {
            this.analyticsService.logGroupMeetingClick(
                'learnMoreAboutGroupMeetings',
                this.analyticsSource,
            )
            this.dialogService.openGroupMeetingsHostBetaAnnouncementDialog()
            return
        }

        this.analyticsService.logGroupMeetingClick('openGroupMeetings', this.analyticsSource)

        if (this.openInDynamicPanel()) {
            this.contentStackService.pushGroupMeetingsToDynamicPanel(false)
            return
        }

        this.router.navigate(['/group-meetings'])
    }

    onDismiss(): void {
        this.dismissedThisSession.set(true)

        if (this.previewMode()) {
            return
        }

        this.analyticsService.logGroupMeetingClick(
            'dismissTryGroupMeetingsBanner',
            this.analyticsSource,
            { isHost: this.isHost() },
        )
        this.localStorageService.setItem(
            this.viewportService.isMobile() ? MOBILE_DISMISSED_KEY : DESKTOP_DISMISSED_KEY,
            'true',
            'GroupMeetingsSidebar',
        )
    }
}
